import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ApiClientSecret } from '../../../../domain/models/ApiClient';
import { useAdminPreferences } from '../../../../application/hooks/useAdminPreferences';
import { formatDate } from '../../../../../../shared/utils/dateUtils';

interface ApiClientSecretModalProps {
  open: boolean;
  onClose: () => void;
  secret: ApiClientSecret | null;
  newSecret: string | null;
}

export const ApiClientSecretModal: React.FC<ApiClientSecretModalProps> = ({
  open,
  onClose,
  secret,
  newSecret,
}) => {
  const [copied, setCopied] = React.useState(false);
  const { language, timezone } = useAdminPreferences();

  const handleCopy = async () => {
    if (newSecret) {
      await navigator.clipboard.writeText(newSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!secret) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Secret Details</DialogTitle>
      <DialogContent>
        {newSecret && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">
                <strong>NEW SECRET GENERATED</strong> - Copy it now and store it securely. This
                value will never be shown again.
              </Typography>
              <IconButton onClick={handleCopy} size="small">
                <ContentCopyIcon />
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              sx={{ mt: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {newSecret}
            </Typography>
            {copied && (
              <Typography variant="caption" color="success.main">
                Copied to clipboard!
              </Typography>
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Secret Hint
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
              {secret.secretHint || 'N/A'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body1" textTransform="capitalize">
              {secret.status.value}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {formatDate(secret.createdAt, { language, timezone, includeTime: true })}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Last Used At
            </Typography>
            <Typography variant="body1">
              {formatDate(secret.lastUsedAt, { language, timezone, includeTime: true })}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Expires At
            </Typography>
            <Typography variant="body1">
              {formatDate(secret.expiresAt, { language, timezone, includeTime: true })}
            </Typography>
          </Box>

          {secret.revokedAt && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Revoked At
              </Typography>
              <Typography variant="body1">
                {formatDate(secret.revokedAt, { language, timezone, includeTime: true })}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
